import { ComponentType, createElement, ReactElement } from "react";

import { IPatternSegment } from "./IPatternSegment";
import { IRoute, RoutePattern, RouteProps } from "./IRoute";
import {
    extractParameters,
    parseRoutePattern,
    routePath,
} from "./RouteFunctions";

type Key = string | number;

export const matchOneOf =
    <TProps extends RouteProps>(
        paths: RoutePattern[] | Record<string, RoutePattern>,
    ) =>
    (component: ComponentType<TProps>) =>
        new MultiRoute(paths, component);

const RouteKey: unique symbol = Symbol("MultiRoute#routeKey");

export const propsForRoute = <TProps extends RouteProps>(
    key: Key,
    props: TProps,
) => ({
    ...props,
    [RouteKey]: key,
});

export class MultiRoute<TProps extends RouteProps> implements IRoute<TProps> {
    private readonly routeGroups: Map<Key, IPatternSegment[]>;

    constructor(
        paths: RoutePattern[] | Record<string, RoutePattern>,
        private readonly component: ComponentType<TProps>,
    ) {
        const pathList = Array.isArray(paths)
            ? paths.map<[number, RoutePattern]>((path, key) => [key, path])
            : Object.entries(paths);

        this.routeGroups = new Map(
            pathList.map(([key, path]) => [key, parseRoutePattern(path)]),
        );
    }

    match(pathWithHash: string): ReactElement<TProps> | false {
        let props: TProps | false = false;
        for (const [, segments] of this.routeGroups) {
            props = extractParameters<TProps>(segments, pathWithHash);
            if (props) {
                break;
            }
        }

        return props && createElement(this.component, props);
    }

    extractParameters(pathWithHash: string): TProps | false {
        for (const [, segments] of this.routeGroups) {
            const props = extractParameters<TProps>(segments, pathWithHash);
            if (props) {
                return props;
            }
        }
        return false;
    }

    toString(
        parameters?: TProps & { [RouteKey]?: Key },
        hashValues?: { [key: string]: any },
    ): string {
        const { [RouteKey]: explicitRouteKey, ...rest } = parameters ?? {};
        if (explicitRouteKey !== undefined) {
            const segments = this.routeGroups.get(explicitRouteKey);
            if (!segments) {
                const validKeys = [...this.routeGroups.keys()]
                    .map(it => `"${it.toString()}"`)
                    .join(", ");
                throw new Error(
                    `Route has no route for "${explicitRouteKey}". Valid route keys are ${validKeys}.`,
                );
            }
            return routePath(segments, rest, hashValues);
        }

        // Attempt to match against all routes…
        const paramKeySet = new Set(Object.keys(rest));
        const matches = [...this.routeGroups.entries()].flatMap<[Key, string]>(
            ([pathKey, segments]) => {
                const routeParams = Object.fromEntries(
                    segments.flatMap(_ => (_.isVariable ? [[_.name, _]] : [])),
                );

                const routeKeySet = new Set(Object.keys(routeParams));
                const isMatch =
                    paramKeySet.size === routeKeySet.size &&
                    [...paramKeySet].every(_ => routeKeySet.has(_));

                if (!isMatch) {
                    return [];
                }

                const hasValidParams = Object.entries(routeParams).every(
                    ([segmentKey, segment]) =>
                        parameters && segment.match(parameters[segmentKey]),
                );

                return hasValidParams
                    ? [[pathKey, routePath(segments, rest, hashValues)]]
                    : [];
            },
        );

        if (matches.length === 0) {
            const keys = [...paramKeySet].join(",");
            throw new Error(`Route has no matching routes: "${keys}"`);
        }

        const [matchedKey, path] = matches[0];
        if (matches.length > 1 && matchedKey !== "default") {
            console.warn(
                `multiple ambiguous paths, choosing first match: "${matchedKey}"`,
            );
        }

        return path;
    }
}
